import { addDoc, collection, deleteDoc, doc, getDoc, query, getDocs, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';

const brandsCollection = collection(db, "brands");

const brandsService = {

  getAll: async () => {
    try {
      const brandsSnapshot = await getDocs(brandsCollection);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return brandsList;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  },

  add: async (brandsData) => {
    try {
      const docRef = await addDoc(brandsCollection, brandsData);
      return docRef;
    } catch (error) {
      console.error('Error adding brand:', error);
      throw error;
    }
  },

  edit: async ({ id, data }) => {
    try {
      const brandRef = doc(db, 'brands', id);
      await updateDoc(brandRef, data);
    } catch (error) {
      console.error('Error updating brands:', error);
      throw error;
    }
  },

  delete: async (brandId) => {
    try {
      await deleteDoc(doc(db, "brands", brandId));
      return true;
    } catch (error) {
      console.error('Error deleting brand:', error);
      throw error;
    }
  },

  getNameById: async (brandId) => {
    if (!brandId) return null;
    try {
      const brandRef = doc(db, 'brands', brandId);
      const brandSnap = await getDoc(brandRef);

      if (brandSnap.exists()) {
        return brandSnap.data().name;
      }
      else {
        return null;
      }
    }
    catch (error) {
      console.error('Error getting brand name:', error);
      throw error;
    }
  },

  getDevisionBrands: async (devisionId) => {
    try {
      const querySnapshot = await getDocs(
        query(brandsCollection, where("division_id", "==", devisionId))
      );

      const brands = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return brands;
    } catch (error) {
      console.error("Error getting brands:", error);
      throw error;
    }
  },

  getTeamsBrands: async (teamId) => {
    try {
      const querySnapshot = await getDocs(
        query(brandsCollection, where("team_id", "==", teamId))
      );

      const brands = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return brands;
    } catch (error) {
      console.error("Error getting Teams:", error);
      throw error;
    }
  },

  /**
   * Check if an order is available for a division + team combination
   */
  isOrderAvailable: async (divisionId, teamId, order, excludeBrandId = null) => {
    try {
      const querySnapshot = await getDocs(
        query(
          brandsCollection,
          where("division_id", "==", divisionId),
          where("team_id", "==", teamId),
          where("order", "==", order)
        )
      );

      const brands = querySnapshot.docs.map(doc => ({ id: doc.id }));
      
      // If excluding a brand (for edit), filter it out
      if (excludeBrandId) {
        return !brands.some(b => b.id !== excludeBrandId);
      }
      
      return brands.length === 0;
    } catch (error) {
      console.error("Error checking order availability:", error);
      throw error;
    }
  },

  /**
   * Get the next order number for a division + team combination
   */
  getNextOrder: async (divisionId, teamId) => {
    try {
      const querySnapshot = await getDocs(
        query(
          brandsCollection,
          where("division_id", "==", divisionId),
          where("team_id", "==", teamId)
        )
      );

      const brands = querySnapshot.docs.map(doc => doc.data());
      
      if (brands.length === 0) {
        return 1;
      }

      const maxOrder = brands.reduce((max, brand) => {
        const order = brand.order ?? 0;
        return order > max ? order : max;
      }, 0);

      return maxOrder + 1;
    } catch (error) {
      console.error("Error getting next order:", error);
      throw error;
    }
  },

  /**
   * Update a brand's order
   */
  updateOrder: async (brandId, newOrder) => {
    try {
      const brandRef = doc(db, 'brands', brandId);
      await updateDoc(brandRef, { order: newOrder });
      return true;
    } catch (error) {
      console.error('Error updating brand order:', error);
      throw error;
    }
  }
};

export default brandsService;
